// Copyright (c) Bentley Systems
import DOMPurify from "dompurify";
import { Dispatch } from "react";
import { Style } from "../DataModels/style";
import {
  getJsonFromParameters,
  getQueryParam,
  getRouteStringFromNodeType,
  getUrlBasedOnEnv,
  setErrorState,
} from "../Helpers/helper-functions";
import { ActionParameterVm } from "./action-parameter-vm";
import { ActionProps } from "./ActionProps";
import { ConfirmationText } from "./confirmation-text";
import { DbCall } from "./db-call";
import { Parameter } from "./parameter";
import { ToastNotification } from "./toast-notification";
import { DawComponentRoutePaths } from "../Helpers/daw-component-route-paths";
import { setIsToastError } from "../app/is-toast-error-slice";
import { ErrorState, setIsError } from "../app/is-error-slice";

/**
 * Query params that represent ambient session context set by the host embedder.
 * These are forwarded on every action navigation regardless of the target node.
 * Add a param here only if it is read by App.tsx or every route — not if it is
 * specific to a single route (e.g. iModelId belongs to the iTwinManagement flow).
 */
const AMBIENT_QUERY_PARAMS: ReadonlySet<string> = new Set([
  "iTwinId",
  "darkTheme",
  "hidemainmenu",
  "hidesidemenu",
  "backToEntryScreen",
]);

export class ActionVm {
  public actionId: number | undefined;
  public caption: string | undefined;
  public isGrayedOut: boolean = false;
  public controlOrder: number | undefined;
  /**EL = Elist, EF = EForm, DB = DbCall, EI = EiCall, ET = ETree, DC = Daw Component */
  public targetNodeType: string | undefined;
  public targetNodeId: number | undefined;
  public targetNodeUniqueName: string | undefined;
  public targetDawComponent: string | undefined;
  /**N = None, D = Delete, S = Save, R = Save and reload
   only for actions on form*/
  public formBehavior: string | undefined;
  public isFormAction: boolean = false;
  public isBackHeaderButton: boolean = false;
  public isBackToEntryScreen: boolean = false;
  /**true = item action, false = header action */
  public isItemAction: boolean | undefined;
  public isInThreeDotMenu: boolean | undefined;
  public actionParameters: ActionParameterVm[] = [];
  public style: Style | undefined;
  public f_ROLL: boolean | undefined;
  public dawPosition: string | undefined;
  public hasConfirmation: boolean = false;
  public confirmationText: ConfirmationText | undefined;
  public isBackToEntryScreenQueryParameter = getQueryParam("backToEntryScreen");
  public toastNotification: ToastNotification | undefined;
  public visFromValue: string | undefined;
  public visFromType: string | undefined;
  public visToValue: string | undefined;
  public visToType: string | undefined;
  public visCompareValue: string | undefined;
  public visCompareType: string | undefined;
  public isVisible: boolean = true;
  public icon: string | undefined;
  public iconBase64Str: string | undefined;
  constructor(initializer?: any) {
    if (!initializer) {
      return;
    }
    if (initializer.hasConfirmation !== undefined) {
      this.hasConfirmation = initializer.hasConfirmation;
    } else {
      if (
        initializer.confirmationType !== undefined &&
        initializer.confirmationType === "Y"
      ) {
        this.hasConfirmation = true;
      }
    }
    if (initializer.confirmationText) {
      this.confirmationText = new ConfirmationText(
        initializer.confirmationText,
      );
    }
    if (initializer.toastNotification) {
      this.toastNotification = new ToastNotification(
        initializer.toastNotification,
      );
    }

    if (initializer.actionId) {
      this.actionId = initializer.actionId;
    }
    if (initializer.caption) {
      this.caption = initializer.caption;
    }
    if (initializer.isDisabled !== undefined) {
      this.isGrayedOut = initializer.isDisabled;
    }
    if (initializer.controlOrder) {
      this.controlOrder = initializer.controlOrder;
    }
    if (initializer.targetNodeType) {
      this.targetNodeType = initializer.targetNodeType;
    }
    if (initializer.targetNodeId) {
      this.targetNodeId = initializer.targetNodeId;
    }
    if (initializer.targetNodeUniqueName) {
      this.targetNodeUniqueName = initializer.targetNodeUniqueName;
    }
    if (initializer.targetDawComponent) {
      this.targetDawComponent = initializer.targetDawComponent;
    }
    if (initializer.formBehavior) {
      this.formBehavior = initializer.formBehavior;
    }
    if (initializer.isItemAction) {
      this.isItemAction = initializer.isItemAction;
    }
    if (initializer.isBackHeaderButton) {
      this.isBackHeaderButton = initializer.isBackHeaderButton;
    }
    if (initializer.isInThreeDotMenu) {
      this.isInThreeDotMenu = initializer.isInThreeDotMenu;
    }
    if (initializer.actionParameters) {
      this.actionParameters = initializer.actionParameters;
    }
    if (initializer.style) {
      this.style = initializer.style;
    }
    if (initializer.f_ROLL) {
      this.f_ROLL = initializer.f_ROLL;
    }
    if (initializer.icon) {
      this.icon = initializer.icon;
    }
    if (initializer.iconBase64Str) {
      this.iconBase64Str = initializer.iconBase64Str;
    }
    if (initializer.visCompareType) {
      this.visCompareType = initializer.visCompareType;
    }
    if (initializer.visCompareValue) {
      this.visCompareValue = initializer.visCompareValue;
    }
    if (initializer.visFromType) {
      this.visFromType = initializer.visFromType;
    }
    if (initializer.visFromValue) {
      this.visFromValue = initializer.visFromValue;
    }
    if (initializer.visToType) {
      this.visToType = initializer.visToType;
    }
    if (initializer.visToValue) {
      this.visToValue = initializer.visToValue;
    }
  }

  public get isSaveAction(): boolean {
    if (this.formBehavior !== undefined && this.formBehavior !== null) {
      if (this.formBehavior === "R" || this.formBehavior === "S") {
        return true;
      }
    }
    return false;
  }

  public get isVisibilityCondition(): boolean {
    return (
      this.visCompareType !== undefined &&
      this.visCompareType !== null &&
      this.visCompareValue !== undefined &&
      this.visCompareValue !== null &&
      this.visFromType !== undefined &&
      this.visFromType !== null &&
      this.visFromValue !== undefined &&
      this.visFromValue !== null &&
      this.visToType !== undefined &&
      this.visToType !== null &&
      this.visToValue !== undefined &&
      this.visToValue !== null
    );
  }

  public async executeAction(
    setLoading: () => void,
    stopLoading: () => void,
    actionProps: ActionProps,
    navigate: (path: string) => void,
    accessToken: string,
    dispatch: Dispatch<any>,
  ) {
    if (setLoading) {
      setLoading();
    }
    try {
      const hasUnresolvedTargetByUniqueName = Boolean(
        this.targetNodeUniqueName &&
          !this.targetNodeType &&
          (this.targetNodeId === undefined || this.targetNodeId === null)
      );

      if (hasUnresolvedTargetByUniqueName) {
        if (stopLoading) {
          stopLoading();
        }

        const actionLabel = this.caption || `#${this.actionId ?? "unknown"}`;
        const toastMessage =
          `Action ${actionLabel} is misconfigured: target node '${this.targetNodeUniqueName}' could not be resolved.`;

        dispatch(
          setIsToastError({
            status: "negative",
            action: "update",
            message: toastMessage,
          }),
        );

        dispatch(
          setIsError({
            errorState: ErrorState.OtherError,
            activityId: "ETREE_ACTION_CONFIG",
            silent: true,
          }),
        );
        return;
      }

      if (actionProps.setActionParametersCurrentFieldsValue !== undefined) {
        actionProps.setActionParametersCurrentFieldsValue(
          actionProps.action.actionParameters,
        );
      }

      const params: Parameter[] = [];
      actionProps.action.actionParameters.forEach((ap) => {
        params.push(new Parameter(ap.name, ap.value));
      });

      if (
        actionProps.action.formBehavior === "S" ||
        actionProps.action.formBehavior === "R"
      ) {
        if (actionProps.saveFunction !== undefined) {
          const saveCompletedSuccessfuly = await actionProps.saveFunction(
            actionProps.action.actionId as number,
            actionProps.action.formBehavior === "S",
          );
          if (stopLoading) {
            stopLoading();
          }
          if (saveCompletedSuccessfuly) {
            await this.navigateToNextNode(
              this.targetNodeId,
              this.targetNodeType,
              params,
              setLoading,
              stopLoading,
              navigate,
              accessToken,
              actionProps,
              dispatch,
            );
          }
        }
      } else {
        if (stopLoading) {
          stopLoading();
        }
        await this.navigateToNextNode(
          this.targetNodeId,
          this.targetNodeType,
          params,
          setLoading,
          stopLoading,
          navigate,
          accessToken,
          actionProps,
          dispatch,
        );
      }
    } finally {
      if (stopLoading) {
        stopLoading();
      }
    }
  }

  public async callStructureMakeCall(
    targetNodeId: number,
    url: string,
    jsonBody: string,
    setLoading: () => void,
    stopLoading: () => void,
    navigate: (path: string) => void,
    accessToken: string,
    actionProps: ActionProps,
    dispatch: Dispatch<any>,
  ) {
    const callStructure = await this.getDbEiReCallStructure(
      targetNodeId,
      setLoading,
      stopLoading,
      accessToken,
      dispatch,
    );
    if (callStructure !== undefined) {
      await this.callDbEiRestCall(
        targetNodeId,
        url,
        jsonBody,
        callStructure.targetNodeId,
        callStructure.targetNodeType,
        setLoading,
        stopLoading,
        accessToken,
        navigate,
        actionProps,
        dispatch,
      );
    }
  }

  public async navigateToNextNode(
    targetNodeId: number | undefined,
    targetNodeType: string | undefined,
    parameters: Parameter[],
    setLoading: () => void,
    stopLoading: () => void,
    navigate: (path: string) => void,
    accessToken: string,
    actionProps: ActionProps,
    dispatch: Dispatch<any>,
  ) {
    if (
      this.isBackHeaderButton &&
      this.isBackToEntryScreen &&
      this.isBackToEntryScreenQueryParameter === "true"
    ) {
      navigate("/entry");
    } else if (
      this.targetDawComponent !== undefined &&
      this.targetDawComponent !== null
    ) {
      const dawComponentString =
        DawComponentRoutePaths[
          this.targetDawComponent as keyof typeof DawComponentRoutePaths
        ];
      this.navigateToUITarget(
        dawComponentString,
        parameters,
        navigate,
        actionProps,
      );
    } else if (
      targetNodeId !== undefined &&
      targetNodeId !== null &&
      targetNodeType !== undefined &&
      targetNodeType !== null
    ) {
      const jsonBody = getJsonFromParameters(parameters);
      switch (targetNodeType) {
        case "DB":
          const urlDb = "/api/Data/GetDbData/";
          await this.callStructureMakeCall(
            targetNodeId,
            urlDb,
            jsonBody,
            setLoading,
            stopLoading,
            navigate,
            accessToken,
            actionProps,
            dispatch,
          );
          break;
        case "EI":
          const urlEi = "/api/Data/GetProjectData/";
          await this.callStructureMakeCall(
            targetNodeId,
            urlEi,
            jsonBody,
            setLoading,
            stopLoading,
            navigate,
            accessToken,
            actionProps,
            dispatch,
          );
          break;
        case "RE":
          const urlRe = "/api/Data/GetRestData/";
          await this.callStructureMakeCall(
            targetNodeId,
            urlRe,
            jsonBody,
            setLoading,
            stopLoading,
            navigate,
            accessToken,
            actionProps,
            dispatch,
          );
          break;

        default: {
          const navigateStr = this.getNavigateStr(targetNodeType, targetNodeId);
          this.navigateToUITarget(
            navigateStr,
            parameters,
            navigate,
            actionProps,
          );
        }
      }
    } else {
      if (actionProps.closeFrollFunction) {
        actionProps.closeFrollFunction(true);
      }
      if (
        this.f_ROLL !== true &&
        actionProps.actionEndedFunction !== undefined
      ) {
        actionProps.actionEndedFunction(true);
      }
    }
  }

  /**navigate to elist or eform with parameters in url */
  public navigateToUITarget(
    navigateStr: string,
    params: Parameter[] = [],
    navigate: (path: string) => void,
    actionProps: ActionProps,
  ) {
    if (this.f_ROLL && actionProps.navigateToInformationPanelFunction) {
      setNavigateWithParams();
      actionProps.navigateToInformationPanelFunction(navigateStr);
    } else {
      if (actionProps.closeFrollFunction) {
        actionProps.closeFrollFunction(
          actionProps.action.formBehavior === "R" ||
            actionProps.action.formBehavior === "S",
        );
      } else {
        //url is the same, parameters can be different

        if (window.location.pathname.indexOf(navigateStr) === -1) {
          setNavigateWithParams();
          navigateStr = this.mergeCurrentSearchParams(navigateStr);
          navigate(navigateStr);
          if (actionProps.actionEndedFunction !== undefined) {
            actionProps.actionEndedFunction(true);
          }
        } else if (actionProps.actionEndedFunction !== undefined) {
          actionProps.actionEndedFunction(true);
        }
      }
    }

    function setNavigateWithParams() {
      if (params.length > 0) {
        navigateStr = `${navigateStr}/`;
        for (let i = 0; i < params.length; i++) {
          const par = params[i];
          if (par.name?.toLowerCase() === "url") {
            par.value = encodeURIComponent(par.value as string);
          }
          const parValue = DOMPurify.sanitize(par.value as string);
          navigateStr = `${navigateStr}${par.name}=${parValue}`;
          if (i < params.length - 1) {
            navigateStr = `${navigateStr}&`;
          }
        }
      }
    }
  }

  /**
   * Forwards only ambient session context params (see AMBIENT_QUERY_PARAMS) to the
   * target URL. Target URL params take precedence over current ones on collision.
   * Route-specific params (e.g. iModelId) are intentionally excluded — the target
   * node either receives them via action path params or does not need them.
   */
  private mergeCurrentSearchParams(navigateStr: string): string {
    if (!window.location.search) {
      return navigateStr;
    }

    const queryStartIndex = navigateStr.indexOf("?");
    const pathWithoutQuery =
      queryStartIndex >= 0 ? navigateStr.slice(0, queryStartIndex) : navigateStr;
    const targetQuery = queryStartIndex >= 0 ? navigateStr.slice(queryStartIndex + 1) : "";

    const currentSearchParams = new URLSearchParams(window.location.search);
    const targetSearchParams = new URLSearchParams(targetQuery);

    const mergedSearchParams = new URLSearchParams();
    currentSearchParams.forEach((value, key) => {
      if (AMBIENT_QUERY_PARAMS.has(key)) {
        mergedSearchParams.set(key, value);
      }
    });
    targetSearchParams.forEach((value, key) => {
      mergedSearchParams.set(key, value);
    });

    const mergedSearch = mergedSearchParams.toString();
    return mergedSearch ? `${pathWithoutQuery}?${mergedSearch}` : pathWithoutQuery;
  }

  private getNavigateStr(targetNodeType: string, targetNodeId: number) {
    const routeString = this.getRouteString(targetNodeType);
    const navigateStr = `/${routeString}/${targetNodeId}`;
    return navigateStr;
  }

  public getRouteString(targetNodeType: string): string {
    if (targetNodeType !== undefined) {
      return getRouteStringFromNodeType(targetNodeType);
    } else {
      return "";
    }
  }

  public async callDbEiRestCall(
    targetNodeId: number,
    urlPath: string,
    jsonBody: string,
    nextNodeId: number | undefined,
    nextNodeType: string | undefined,
    setLoading: () => void,
    stopLoading: () => void,
    accessToken: string,
    navigate: (path: string) => void,
    actionProps: ActionProps,
    dispatch: Dispatch<any>,
  ) {
    setLoading();
    const url = getUrlBasedOnEnv(urlPath + targetNodeId);
    const callRunId = crypto.randomUUID();
    const iTwinQueryParam = getQueryParam("iTwinId");
    await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken,
        RunId: callRunId,
        ...(iTwinQueryParam && { "Itwin-ID": iTwinQueryParam }),
      },
      body: jsonBody,
    })
      .then(async function (response) {
        if (!response.ok) {
          setErrorState(response.status, dispatch, callRunId, true);
          throw new Error("callDbEiRestCall failed");
        } else {
          return response.json();
        }
      })
      .then(async (data) => {
        stopLoading();
        if (!data) {
          throw new Error("callDbEiRestCall failed");
        }
        let parameters: Parameter[] = [];
        Object.keys(data).forEach((key) => {
          parameters.push(new Parameter(key, data[key]));
        });

        if (nextNodeType === null) {
          const parNodeType = parameters.find(
            (p) => p.name === "targetNodeType",
          );
          if (parNodeType !== undefined && parNodeType.value !== undefined) {
            nextNodeType = parNodeType.value;
            // do not send parameter to next node as input => forbidden
            parameters = parameters.filter((p) => p.name !== "targetNodeType");
          }
        }
        if (nextNodeId === null) {
          const parNodeId = parameters.find((p) => p.name === "targetNodeId");
          if (parNodeId !== undefined && parNodeId.value !== undefined) {
            nextNodeId = +parNodeId.value;
            parameters = parameters.filter((p) => p.name !== "targetNodeId");
          }
        }
        await this.navigateToNextNode(
          nextNodeId,
          nextNodeType,
          parameters,
          setLoading,
          stopLoading,
          navigate,
          accessToken,
          actionProps,
          dispatch,
        );
      });
  }

  public async getDbEiReCallStructure(
    targetNodeId: number,
    setLoading: () => void,
    stopLoading: () => void,
    accessToken: string,
    dispatch: Dispatch<any>,
  ): Promise<DbCall | undefined> {
    setLoading();
    const jsonBody = "{}";
    const url = getUrlBasedOnEnv(
      `/api/Structure/GetNodeConfiguration/${targetNodeId}`,
    );
    const dbCall = new DbCall();
    const confRunId = crypto.randomUUID();
    const iTwinQueryParam = getQueryParam("iTwinId");
    await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken,
        RunId: confRunId,
        ...(iTwinQueryParam && { "Itwin-ID": iTwinQueryParam }),
      },
      body: jsonBody,
    })
      .then(async function (response) {
        if (!response.ok) {
          setErrorState(response.status, dispatch, confRunId, true);
          throw new Error("getDbEiReCallStructure failed");
        } else {
          return response.json();
        }
      })
      .then(async (data) => {
        stopLoading();
        if (data !== null && data !== undefined) {
          dbCall.targetNodeType = data.targetNodeType;
          dbCall.targetNodeId = data.targetNodeId;
        }
      });
    return dbCall;
  }
}
